import { ORGANIZATIONS_ACTION_TYPES } from "../_actionTypes/ORGANIZATIONS_ACTION_TYPES";
import {
    Organization,
    OrganizationDetail,
    OrganizationLocationRef,
} from "../_models/Organization";

export interface AvailableLocationsPage {
    items: OrganizationLocationRef[];
    total: number;
    // Search term the stored page belongs to (null = unfiltered): appends
    // from a different filter replace the page instead of extending it.
    searchTerm: string | null;
}

export interface OrganizationsState {
    items: Organization[];
    selectedUid: string | null;
    detailByUid: { [uid: string]: OrganizationDetail };
    availableLocationsByUid: { [uid: string]: AvailableLocationsPage };
    // Id of the latest available-locations request per organization: a
    // success carrying an older id is a stale response (a slower request
    // overtaken by a newer one) and is discarded, so it can neither replace
    // the newest page nor leave searchTerm pointing at an abandoned filter.
    availableLocationsRequestIdByUid: { [uid: string]: number };
    isRequesting: boolean;
    isRequestingDetail: boolean;
    // True only after a successful list: consumers that pick the active
    // organization must not mistake "not loaded / failed" for "the user has
    // no organizations" (they would silently fall back to the personal
    // scope).
    hasLoaded: boolean;
    // True after a list request failed and no successful one has landed
    // since. Lets consumers tell "still loading" from "could not load", and
    // decide per case: the modules stay strict (editing the wrong scope is
    // worse than not editing), the header falls back to the personal
    // branding rather than showing none at all.
    hasFailed: boolean;
}

const initialState: OrganizationsState = {
    items: [],
    selectedUid: null,
    detailByUid: {},
    availableLocationsByUid: {},
    availableLocationsRequestIdByUid: {},
    isRequesting: false,
    isRequestingDetail: false,
    hasLoaded: false,
    hasFailed: false,
};

let getRootState = (state: any): OrganizationsState => state;

export class OrganizationsSelector {
    static getOrganizations = (state) => getRootState(state).items;
    static getSelectedUid = (state) => getRootState(state).selectedUid;
    static getSelectedDetail = (state) => {
        const uid = getRootState(state).selectedUid;
        return uid ? getRootState(state).detailByUid[uid] : null;
    };
    static getAvailableLocationsForSelected = (state) => {
        const uid = getRootState(state).selectedUid;
        return uid
            ? getRootState(state).availableLocationsByUid[uid]?.items ?? []
            : [];
    };
    static getAvailableLocationsTotalForSelected = (state) => {
        const uid = getRootState(state).selectedUid;
        return uid
            ? getRootState(state).availableLocationsByUid[uid]?.total ?? 0
            : 0;
    };
    static getAvailableLocationsSearchTermForSelected = (state) => {
        const uid = getRootState(state).selectedUid;
        return uid
            ? getRootState(state).availableLocationsByUid[uid]?.searchTerm ??
                  null
            : null;
    };
    static getIsRequesting = (state) => getRootState(state).isRequesting;
    static getIsRequestingDetail = (state) =>
        getRootState(state).isRequestingDetail;
    static getHasLoaded = (state) => getRootState(state).hasLoaded;
    static getHasFailed = (state) => getRootState(state).hasFailed;
    static getIsRequestingList = (state) => getRootState(state).isRequesting;

    static setRootStateFn = (
        rootStateFn: (state: any) => OrganizationsState,
    ) => {
        getRootState = rootStateFn;
    };
}

export function organizations(
    state: OrganizationsState = initialState,
    action: any,
): OrganizationsState {
    switch (action.type) {
        case ORGANIZATIONS_ACTION_TYPES.LIST_REQUEST:
            return { ...state, isRequesting: true, hasFailed: false };

        case ORGANIZATIONS_ACTION_TYPES.LIST_SUCCESS:
            return {
                ...state,
                items: action.items,
                isRequesting: false,
                hasLoaded: true,
                hasFailed: false,
            };

        case ORGANIZATIONS_ACTION_TYPES.LIST_FAILURE:
            return { ...state, isRequesting: false, hasFailed: true };

        case ORGANIZATIONS_ACTION_TYPES.SELECT:
            return { ...state, selectedUid: action.uid };

        case ORGANIZATIONS_ACTION_TYPES.DETAIL_REQUEST:
            return { ...state, isRequestingDetail: true };

        case ORGANIZATIONS_ACTION_TYPES.DETAIL_SUCCESS: {
            const detail: OrganizationDetail = action.detail;
            return {
                ...state,
                isRequestingDetail: false,
                detailByUid: {
                    ...state.detailByUid,
                    [detail.uid]: detail,
                },
            };
        }

        case ORGANIZATIONS_ACTION_TYPES.DETAIL_FAILURE:
            return { ...state, isRequestingDetail: false };

        case ORGANIZATIONS_ACTION_TYPES.AVAILABLE_LOCATIONS_REQUEST:
            return {
                ...state,
                availableLocationsRequestIdByUid: {
                    ...state.availableLocationsRequestIdByUid,
                    [action.uid]: action.requestId,
                },
            };

        case ORGANIZATIONS_ACTION_TYPES.AVAILABLE_LOCATIONS_SUCCESS: {
            // Stale response: a newer request was issued after this one, so
            // its outcome (or the cleared page) must win regardless of which
            // response reached the network first.
            const latestRequestId =
                state.availableLocationsRequestIdByUid[action.uid];
            if (
                latestRequestId !== undefined &&
                action.requestId !== undefined &&
                action.requestId !== latestRequestId
            ) {
                return state;
            }
            const previous = state.availableLocationsByUid[action.uid];
            const searchTerm = action.searchTerm ?? null;
            const sameSearch =
                !!previous && previous.searchTerm === searchTerm;
            // Offset continuity: a duplicate response for a page already
            // appended (double-clicked load-more) targets an offset the list
            // has grown past — ignore it, appending would duplicate rows and
            // replacing would shrink the list to that single page.
            if (
                action.append &&
                sameSearch &&
                action.offset !== undefined &&
                previous.items.length !== action.offset
            ) {
                return state;
            }
            const canAppend = action.append && sameSearch;
            const items = canAppend
                ? [...previous.items, ...action.locations]
                : action.locations;
            return {
                ...state,
                availableLocationsByUid: {
                    ...state.availableLocationsByUid,
                    [action.uid]: {
                        items,
                        total: action.total ?? items.length,
                        searchTerm,
                    },
                },
            };
        }

        default:
            return state;
    }
}
